import { html, TemplateResult } from 'lit';
import {
  SHELL_CATALOG_SYNC_BUSY_EVENT,
  SHELL_CATALOG_SYNC_IDLE_EVENT,
  SHELL_PENDING_SYNC_CHANGED_EVENT,
  SHELL_PORTS_UPDATED_EVENT,
} from '@core/domain/sessionEvents.ts';
import { isCatalogSyncActive } from '@core/bootstrap/factories/postLoginCatalogSync.ts';
import { syncCatalogNeedsDownload } from '@src/modules/system/domain/syncCatalogAlert.ts';
import {
  isShellMoreNavPath,
  normalizeShellPath,
  SHELL_BOTTOM_NAV_ITEMS,
  SHELL_MORE_NAV_ITEMS,
  SHELL_MORE_NAV_PATH,
  SHELL_NAV_ITEMS,
  type ShellNavItem,
} from '@src/modules/shell-routing/domain/shellNavConfig.ts';
import type { ShellCorePorts } from '@src/core/bootstrap/ShellPorts.ts';
import { announceShellRoute } from '@src/modules/shell-routing/infrastructure/shellNavMotion.ts';
import { loadPendingSyncRecordCount } from '@src/modules/shell-ui/infrastructure/shellContentUi.ts';
import { syncHtmlDialogOpen } from '@src/modules/shell-ui/infrastructure/shellDialogUi.ts';
import { BRAND_UI } from 'brand-ui-assets';
import { LitComponent } from '@src/modules/shell-ui/infrastructure/drivers/ui/components/LitComponent.ts';
import { shellPwaInstallGuideTemplate, shellIosWeeklyReminderTemplate } from '@src/modules/shell-ui/infrastructure/shellPwaInstallGuideUi.ts';
import {
  dismissPwaStabilityHint,
  dismissIosWeeklyReminder,
  getPwaInstallGuidanceKind,
  isIosWeeklyReminderDismissed,
  isPwaStabilityHintDismissed,
  recordIosPwaOpenAt,
  shouldShowPwaStabilityHint,
  type PwaInstallGuidanceKind,
} from '@src/modules/shell-ui/infrastructure/shellPwaInstallUi.ts';
import { pushShellBackHandler } from '@src/modules/shell-ui/infrastructure/shellBackPress.ts';

import { SHELL_NAV_ICON_DEFAULT, SHELL_NAV_ICON_PATHS } from '@src/modules/shell-routing/domain/shellNavIcons.ts';

/** Logs out with telemetry/toast on failure and redirects to login. */
async function runLayoutLogoutAndRedirect(
  shellPorts: Pick<ShellCorePorts, 'auth' | 'telemetry' | 'notifications'>,
  loginPath: string = '/login',
): Promise<void> {
  try {
    await shellPorts.auth.logout();
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    shellPorts.telemetry.logError(msg, { source: 'layout.logout', error: e });
    shellPorts.notifications.warning('Cierre de sesión incompleto; se redirige al login.');
  }
  globalThis.location.replace(loginPath);
}

function shellNavIcon(path: string): TemplateResult {
  const d = SHELL_NAV_ICON_PATHS[path] ?? SHELL_NAV_ICON_DEFAULT;
  return html`<svg class="h-6 w-6 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="${d}" /></svg>`;
}

function shellNavLinkClass(active: boolean, variant: 'bottom' | 'rail'): string {
  const tone = active
    ? 'text-[color:var(--md-sys-color-primary)]'
    : 'text-[color:var(--md-sys-color-on-surface-variant)]';
  if (variant === 'rail') {
    return `md-sys-ripple touch-manipulation flex min-h-[4.5rem] w-full flex-col items-center justify-center gap-1 px-2 py-3 text-center outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--md-sys-color-primary)] ${tone}`;
  }
  return `md-sys-ripple touch-manipulation flex min-h-[3.5rem] flex-1 flex-col items-center justify-center gap-1 px-1 py-2 text-center outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--md-sys-color-primary)] ${tone}`;
}

function shellNavBadge(p: { path: string; pendingSyncCount: number; syncCatalogAlert: boolean }): TemplateResult {
  const showOnSync = p.path === '/sync' && (p.pendingSyncCount > 0 || p.syncCatalogAlert);
  const showOnMore = p.path === SHELL_MORE_NAV_PATH && (p.pendingSyncCount > 0 || p.syncCatalogAlert);
  if (!showOnSync && !showOnMore) return html``;
  const label =
    p.pendingSyncCount > 0
      ? `${p.pendingSyncCount} registro(s) por sincronizar`
      : 'Atención: sincronización de catálogos';
  const text = p.pendingSyncCount > 0 ? (p.pendingSyncCount > 99 ? '99+' : String(p.pendingSyncCount)) : '!';
  return html`<span class="shell-pending-sync-badge" aria-label="${label}">${text}</span>`;
}

function shellNavDestinations(p: {
  navItems: readonly ShellNavItem[];
  currentPath: string;
  pendingSyncCount: number;
  syncCatalogAlert: boolean;
  moreMenuActive: boolean;
  variant: 'bottom' | 'rail';
  onOpenMore?: () => void;
}): TemplateResult {
  return html`
    ${p.navItems.map((item) => {
      const isMore = item.path === SHELL_MORE_NAV_PATH;
      const active = isMore ? p.moreMenuActive || isShellMoreNavPath(p.currentPath) : p.currentPath === item.path;
      const badgeArgs = { path: item.path, pendingSyncCount: p.pendingSyncCount, syncCatalogAlert: p.syncCatalogAlert };
      const label = p.variant === 'bottom' ? (item.shortLabel ?? item.label) : item.label;
      const iconShell = html`
        <span class="shell-nav-dest-icon-shell">
          ${shellNavIcon(item.path)}
          ${shellNavBadge(badgeArgs)}
        </span>
        <span class="shell-nav-dest-label max-w-full truncate px-0.5">${label}</span>
      `;
      const vibrate = (): void => {
        try {
          navigator.vibrate?.(12);
        } catch {
          /* WebView / policy may block vibration. */
        }
      };
      if (isMore && p.onOpenMore) {
        return html`
          <li class="min-w-0 flex-1">
            <button
              type="button"
              class="${shellNavLinkClass(active, p.variant)} w-full border-0 bg-transparent"
              aria-expanded=${p.moreMenuActive ? 'true' : 'false'}
              aria-haspopup="dialog"
              title=${item.label}
              @click=${() => {
                vibrate();
                p.onOpenMore!();
              }}
            >
              ${iconShell}
            </button>
          </li>
        `;
      }
      return html`
        <li class="${p.variant === 'bottom' ? 'min-w-0 flex-1' : 'w-full'}">
          <a
            href="${item.path}"
            class="${shellNavLinkClass(active, p.variant)}"
            aria-current=${active ? 'page' : undefined}
            title=${item.label}
            @click=${vibrate}
          >
            ${iconShell}
          </a>
        </li>
      `;
    })}
  `;
}

function shellMoreMenuTemplate(p: {
  currentPath: string;
  pendingSyncCount: number;
  syncCatalogAlert: boolean;
  onClose: () => void;
}): TemplateResult {
  return html`
    <dialog id="shell-more-menu" class="shell-more-sheet" @cancel=${(e: Event) => { e.preventDefault(); p.onClose(); }}>
      <div class="shell-more-sheet__body">
        <div class="shell-more-sheet__header">
          <h2 class="shell-more-sheet__title">Más opciones</h2>
          <button type="button" class="md-btn-icon md-sys-ripple touch-manipulation" aria-label="Cerrar" @click=${p.onClose}>
            <svg class="md-btn-icon__glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <ul class="shell-more-sheet__list m-0 list-none p-0">
          ${SHELL_MORE_NAV_ITEMS.map((item) => {
            const active = p.currentPath === item.path;
            return html`
              <li>
                <a
                  href="${item.path}"
                  class="shell-more-sheet__link md-sys-ripple ${active ? 'shell-more-sheet__link--active' : ''}"
                  aria-current=${active ? 'page' : undefined}
                  @click=${p.onClose}
                >
                  <span class="shell-nav-dest-icon-shell">
                    ${shellNavIcon(item.path)}
                    ${shellNavBadge({ path: item.path, pendingSyncCount: p.pendingSyncCount, syncCatalogAlert: p.syncCatalogAlert })}
                  </span>
                  <span class="min-w-0 flex-1">
                    <span class="block text-base font-medium">${item.label}</span>
                    ${item.path === '/sync' && p.pendingSyncCount > 0
                      ? html`<span class="text-sm text-[color:var(--md-sys-color-on-surface-variant)]">${p.pendingSyncCount} por enviar</span>`
                      : null}
                  </span>
                </a>
              </li>
            `;
          })}
        </ul>
      </div>
    </dialog>
  `;
}

export function layoutComponentTemplate(p: {
  appTitle: string;
  railNavItems: readonly ShellNavItem[];
  bottomNavItems: readonly ShellNavItem[];
  currentPath: string;
  pendingSyncCount: number;
  syncCatalogAlert: boolean;
  moreMenuOpen: boolean;
  headerElevated: boolean;
  online: boolean;
  onLogout: () => void;
  onMainScroll: (e: Event) => void;
  onOpenMore: () => void;
  onCloseMore: () => void;
  catalogSyncBusy?: boolean;
  pwaInstallGuide: PwaInstallGuidanceKind | null;
  onDismissPwaInstallGuide: () => void;
  iosWeeklyReminder: boolean;
  onDismissIosWeeklyReminder: () => void;
}): TemplateResult {
  const headerLogoSrc = BRAND_UI.appHeaderLogo;
  const badgeArgs = { pendingSyncCount: p.pendingSyncCount, syncCatalogAlert: p.syncCatalogAlert };
  return html`
    <div
      class="shell-root flex h-[100dvh] max-h-[100dvh] min-h-0 flex-col overflow-hidden bg-[color:var(--md-sys-color-surface)] text-[color:var(--md-sys-color-on-surface)]"
    >
      <header
        role="banner"
        class="shell-app-header sticky top-0 z-30 shrink-0 border-b border-[color:var(--md-sys-color-outline-variant)] bg-[color:var(--md-sys-color-surface-container)] shadow-[var(--md-elevation-1)] ${p.headerElevated ? 'shell-header-elevated' : ''}"
        style="padding-top: max(0.75rem, env(safe-area-inset-top)); transition: box-shadow var(--md-motion-duration-short2) var(--md-motion-easing-standard);"
      >
        <div class="flex min-h-14 items-center gap-3 px-4 py-2">
          <div class="flex min-w-0 flex-1 items-center gap-3">
            <img
              class="shell-brand-logo h-9 w-auto shrink-0 object-contain"
              src="${headerLogoSrc}"
              width="36"
              height="36"
              alt="Paquetes"
              decoding="async"
            />
            <div class="min-w-0 flex-1">
              <h1
                class="min-w-0 truncate text-[length:1.25rem] font-normal leading-snug tracking-tight text-[color:var(--md-sys-color-on-surface)]"
              >
                ${p.appTitle}
              </h1>
              ${p.online
                ? p.pendingSyncCount > 0
                  ? html`<a href="/sync" class="shell-header-pending-chip" role="status">${p.pendingSyncCount} pendiente${p.pendingSyncCount === 1 ? '' : 's'}</a>`
                  : null
                : html`<span class="shell-offline-chip" role="status">
                    <span class="shell-offline-chip__dot" aria-hidden="true"></span>
                    Sin conexión
                  </span>`}
            </div>
          </div>
          <button
            type="button"
            class="md-sys-ripple touch-manipulation flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-[color:var(--md-sys-color-on-surface-variant)] outline-none hover:bg-black/[0.06] focus-visible:ring-2 focus-visible:ring-[color:var(--md-sys-color-primary)]"
            aria-label="Cerrar sesión"
            @click=${p.onLogout}
          >
            <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
              />
            </svg>
          </button>
        </div>
      </header>

      ${p.pwaInstallGuide
        ? html`
            <div class="shrink-0 border-b border-[color:var(--md-sys-color-outline-variant)] px-4 py-3">
              ${shellPwaInstallGuideTemplate(p.pwaInstallGuide, {
                dismissible: true,
                onDismiss: p.onDismissPwaInstallGuide,
              })}
            </div>
          `
        : null}

      ${p.iosWeeklyReminder
        ? html`
            <div class="shrink-0 border-b border-[color:var(--md-sys-color-outline-variant)] px-4 py-3">
              ${shellIosWeeklyReminderTemplate(p.onDismissIosWeeklyReminder)}
            </div>
          `
        : null}

      <div class="shell-body flex min-h-0 flex-1 flex-col lg:flex-row">
        ${p.railNavItems.length > 0
          ? html`
              <nav
                aria-label="Módulos"
                class="layout-rail-nav shell-rail-nav hidden shrink-0 border-r border-[color:var(--md-sys-color-outline-variant)] bg-[color:var(--md-sys-color-surface-container)] shadow-[var(--md-elevation-1)] lg:flex lg:w-[5.5rem] lg:flex-col"
                style="padding-top: 0.5rem; padding-bottom: max(0.5rem, env(safe-area-inset-bottom));"
              >
                <ul class="flex h-full w-full list-none flex-col p-0 m-0">
                  ${shellNavDestinations({
                    navItems: p.railNavItems,
                    currentPath: p.currentPath,
                    moreMenuActive: p.moreMenuOpen,
                    variant: 'rail',
                    ...badgeArgs,
                  })}
                </ul>
              </nav>
            `
          : null}

        <main class="layout-main-scroll min-h-0 min-w-0 flex-1 overflow-auto" @scroll=${p.onMainScroll}>
          <div id="main" class="shell-main-host px-4 py-3 lg:px-6 lg:py-4"></div>
        </main>
      </div>

      ${p.bottomNavItems.length > 0
        ? html`
            <nav
              aria-label="Módulos"
              class="layout-bottom-nav shell-bottom-nav shrink-0 border-t border-[color:var(--md-sys-color-outline-variant)] bg-[color:var(--md-sys-color-surface-container)] shadow-[var(--md-elevation-2)] lg:hidden"
              style="padding-bottom: max(0.25rem, env(safe-area-inset-bottom));"
            >
              <ul class="flex w-full list-none p-0 m-0">
                ${shellNavDestinations({
                  navItems: p.bottomNavItems,
                  currentPath: p.currentPath,
                  moreMenuActive: p.moreMenuOpen,
                  variant: 'bottom',
                  onOpenMore: p.onOpenMore,
                  ...badgeArgs,
                })}
              </ul>
            </nav>
            ${shellMoreMenuTemplate({
              currentPath: p.currentPath,
              pendingSyncCount: p.pendingSyncCount,
              syncCatalogAlert: p.syncCatalogAlert,
              onClose: p.onCloseMore,
            })}
          `
        : null}
      ${p.catalogSyncBusy
        ? html`
            <div class="shell-sync-overlay" role="alertdialog" aria-modal="true" aria-busy="true" aria-live="polite" aria-label="Sincronizando catálogos">
              <div class="shell-sync-overlay__card">
                <div class="shell-sync-overlay__spinner" aria-hidden="true"></div>
                <p class="m-0 text-sm font-medium">Sincronizando catálogos…</p>
                <p class="m-0 text-xs text-[color:var(--md-sys-color-on-surface-variant)]">Descargando datos actualizados del servidor</p>
              </div>
            </div>
          `
        : null}
    </div>
  `;
}

export class LayoutComponent extends LitComponent {
  shellPorts!: ShellCorePorts;

  private currentPath = '/';
  private pendingSyncCount = 0;
  private syncCatalogAlert = false;
  private moreMenuOpen = false;
  private headerElevated = false;
  private online = globalThis.navigator?.onLine ?? true;
  private catalogSyncBusy = false;
  private pwaHintDismissed = isPwaStabilityHintDismissed();
  private iosWeeklyReminder = false;
  private iosWeeklyReminderDismissed = isIosWeeklyReminderDismissed();
  private detachLayoutBackPress: (() => void) | null = null;

  private readonly onDismissPwaInstallGuide = (): void => {
    dismissPwaStabilityHint();
    this.pwaHintDismissed = true;
    this.update();
  };

  private readonly onDismissIosWeeklyReminder = (): void => {
    dismissIosWeeklyReminder();
    this.iosWeeklyReminderDismissed = true;
    this.iosWeeklyReminder = false;
    this.update();
  };

  private readonly onCatalogSyncBusy = (): void => {
    this.catalogSyncBusy = true;
    this.update();
  };

  private readonly onCatalogSyncIdle = (): void => {
    this.catalogSyncBusy = false;
    void this.refreshNav();
  };

  private readonly handleLogout = (): void => {
    void runLayoutLogoutAndRedirect(this.shellPorts);
  };

  private readonly onNavigate = (): void => {
    this.moreMenuOpen = false;
    void this.refreshNav();
  };

  private readonly onShellPortsUpdated = (): void => {
    void this.refreshNav();
  };

  private readonly onPendingSyncChanged = (): void => {
    void this.refreshNav();
  };

  private readonly onMainScroll = (e: Event): void => {
    const target = e.currentTarget as HTMLElement;
    const elevated = target.scrollTop > 4;
    if (elevated === this.headerElevated) return;
    this.headerElevated = elevated;
    this.update();
  };

  private readonly onConnectivityChange = (): void => {
    const next = globalThis.navigator.onLine;
    if (next === this.online) return;
    this.online = next;
    announceShellRoute(next ? 'Conexión restablecida' : 'Sin conexión');
    this.update();
  };

  private readonly onOpenMore = (): void => {
    this.moreMenuOpen = true;
    this.update();
  };

  private readonly onCloseMore = (): void => {
    this.moreMenuOpen = false;
    this.update();
  };

  connectedCallback(): void {
    if (!this.shellPorts) {
      throw new Error('layout-component: falta shellPorts (montaje del shell)');
    }
    this.syncCurrentPath();
    if (isCatalogSyncActive()) this.catalogSyncBusy = true;
    if (!this.iosWeeklyReminderDismissed) {
      this.iosWeeklyReminder = recordIosPwaOpenAt() === 'remind';
    }
    super.connectedCallback();
    void this.refreshNav();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
  }

  protected setupEventListeners(): void {
    globalThis.addEventListener('navigate', this.onNavigate);
    globalThis.addEventListener('popstate', this.onNavigate);
    globalThis.addEventListener('online', this.onConnectivityChange);
    globalThis.addEventListener('offline', this.onConnectivityChange);
    globalThis.addEventListener(SHELL_PORTS_UPDATED_EVENT, this.onShellPortsUpdated);
    globalThis.addEventListener(SHELL_PENDING_SYNC_CHANGED_EVENT, this.onPendingSyncChanged);
    globalThis.addEventListener(SHELL_CATALOG_SYNC_BUSY_EVENT, this.onCatalogSyncBusy);
    globalThis.addEventListener(SHELL_CATALOG_SYNC_IDLE_EVENT, this.onCatalogSyncIdle);
    this.detachLayoutBackPress?.();
    this.detachLayoutBackPress = pushShellBackHandler(() => {
      if (this.moreMenuOpen) {
        this.onCloseMore();
        return true;
      }
      return false;
    });
  }

  protected removeEventListeners(): void {
    globalThis.removeEventListener('navigate', this.onNavigate);
    globalThis.removeEventListener('popstate', this.onNavigate);
    globalThis.removeEventListener('online', this.onConnectivityChange);
    globalThis.removeEventListener('offline', this.onConnectivityChange);
    globalThis.removeEventListener(SHELL_PORTS_UPDATED_EVENT, this.onShellPortsUpdated);
    globalThis.removeEventListener(SHELL_PENDING_SYNC_CHANGED_EVENT, this.onPendingSyncChanged);
    globalThis.removeEventListener(SHELL_CATALOG_SYNC_BUSY_EVENT, this.onCatalogSyncBusy);
    globalThis.removeEventListener(SHELL_CATALOG_SYNC_IDLE_EVENT, this.onCatalogSyncIdle);
    this.detachLayoutBackPress?.();
    this.detachLayoutBackPress = null;
  }

  private syncCurrentPath(): void {
    const path = this.shellPorts.navigation.getCurrentPath() || globalThis.location.pathname;
    this.currentPath = normalizeShellPath(path);
  }

  private async refreshNav(): Promise<void> {
    this.syncCurrentPath();
    try {
      const [status, pendingSyncCount] = await Promise.all([
        this.shellPorts.system.getStatus(),
        loadPendingSyncRecordCount(this.shellPorts.system),
      ]);
      this.pendingSyncCount = pendingSyncCount;
      this.syncCatalogAlert = syncCatalogNeedsDownload(status);
    } catch {
      this.pendingSyncCount = 0;
      this.syncCatalogAlert = false;
    }
    this.update();
  }

  protected update(): void {
    super.update();
    queueMicrotask(() => {
      syncHtmlDialogOpen(this.shadow?.querySelector('#shell-more-menu') as HTMLDialogElement | null, this.moreMenuOpen);
    });
  }

  protected getTemplate(): TemplateResult {
    return layoutComponentTemplate({
      appTitle: this.shellPorts.appDisplayTitle,
      railNavItems: SHELL_NAV_ITEMS,
      bottomNavItems: SHELL_BOTTOM_NAV_ITEMS,
      currentPath: this.currentPath,
      pendingSyncCount: this.pendingSyncCount,
      syncCatalogAlert: this.syncCatalogAlert,
      moreMenuOpen: this.moreMenuOpen,
      headerElevated: this.headerElevated,
      online: this.online,
      onLogout: this.handleLogout,
      onMainScroll: this.onMainScroll,
      onOpenMore: this.onOpenMore,
      onCloseMore: this.onCloseMore,
      catalogSyncBusy: this.catalogSyncBusy,
      pwaInstallGuide:
        shouldShowPwaStabilityHint() && !this.pwaHintDismissed ? getPwaInstallGuidanceKind() : null,
      onDismissPwaInstallGuide: this.onDismissPwaInstallGuide,
      iosWeeklyReminder: this.iosWeeklyReminder,
      onDismissIosWeeklyReminder: this.onDismissIosWeeklyReminder,
    });
  }
}

customElements.define('layout-component', LayoutComponent);
